import gsap from 'gsap';

import { getRootOffset, lerp } from '../../helpers';

export default class Cursor {

	isDrag = false;

	textColor = getComputedStyle(document.documentElement).getPropertyValue('--text-color');

	constructor(container) {
		this.el = {
			container,
			inner: container.querySelector('.cursor__inner'),
			label: container.querySelector('.cursor__label'),
		};

		this.scale = {
			value: 0.34,
			target: 0.34
		};

		this.position = {
			x: window.innerWidth / 2,
			y: window.innerHeight / 2,
		};

		this.mouse = {
			x: window.innerWidth / 2,
			y: window.innerHeight / 2,
		};
	}

	#onTargetMouseEnter = (e) => {
		const { currentTarget } = e;

		if (!e.currentTarget.hasAttribute('data-cursor-disabled')) {
			this.focus = currentTarget.querySelector('[data-cursor-focus]');

			if (this.focus) {
				this.scale.target = 0.67;
				this.el.container.classList.add('is-active');
			}
		} else {
			this.el.container.classList.add('is-active-disabled');
		}
	}

	#onTargetMouseLeave = () => {
		this.focus = null;

		if (!this.isDrag) {
			this.scale.target = 0.35;
			this.el.container.classList.remove('is-active');
		} else {
			this.scale.target = 1;
		}

		this.el.container.classList.remove('is-active-disabled');
	}

	#onDragMouseEnter = (e) => {
		if (!this.isDrag) {
			this.isDrag = true;
			this.scale.target = 1;
			this.el.container.classList.add('is-active-drag');

			if (!e.currentTarget.hasAttribute('data-slider-images')) {
				this.el.container.classList.add('is-active-drag-no-images');
			}

			if (e.currentTarget.hasAttribute('data-cursor-next')) {
				this.el.label.innerHTML = 'View';
			} else {
				this.el.label.innerHTML = 'Drag';
			}
		}
	}

	#onDragMouseLeave = () => {
		this.isDrag = false;
		this.scale.target = 0.35;
		this.el.container.classList.remove('is-active-drag');
		this.el.container.classList.remove('is-active-drag-no-images');
		this.el.label.innerHTML = 'Drag';
	}

	#onDotMouseEnter = (e) => {
		this.scale.target = 0.1;
		this.dot = e.currentTarget;
		this.el.container.classList.add('is-active-dot');
	}

	#onDotMouseLeave = () => {
		this.scale.target = 0.35;
		this.dot = null;
		this.el.container.classList.remove('is-active-dot');
	}

	mouseMove({ x, y }) {
		this.mouse.x = x;
		this.mouse.y = y;
	}

	mouseDown() {
	}

	mouseUp() {
	}

	resize() {
	}

	complete() {
		gsap.to(this.el.container, 1, {
			autoAlpha: 0.2,
			delay: 3
		});

		this.targets = document.querySelectorAll('[data-cursor-target]');
		this.drags = document.querySelectorAll('[data-cursor-drag]');
		this.dots = document.querySelectorAll('[data-cursor-dot]');

		if (this.targets.length) {
			for (const target of this.targets) {
				target.addEventListener('mouseenter', this.#onTargetMouseEnter);
				target.addEventListener('mouseleave', this.#onTargetMouseLeave);
			}
		}

		if (this.drags.length) {
			for (const drag of this.drags) {
				drag.addEventListener('mouseenter', this.#onDragMouseEnter);
				drag.addEventListener('mouseleave', this.#onDragMouseLeave);
			}
		}

		if (this.dots.length) {
			for (const dot of this.dots) {
				dot.addEventListener('mouseenter', this.#onDotMouseEnter);
				dot.addEventListener('mouseleave', this.#onDotMouseLeave);
			}
		}
	}

	reset() {
		this.isDrag = false;
		this.scale.target = 0.35;
		this.focus = null;
		this.dot = null;

		if (this.targets && this.targets.length) {
			for (const target of this.targets) {
				target.removeEventListener('mouseenter', this.#onTargetMouseEnter);
				target.removeEventListener('mouseleave', this.#onTargetMouseLeave);
			}
			this.targets = null;
		}

		if (this.drags && this.drags.length) {
			for (const drag of this.drags) {
				drag.removeEventListener('mouseenter', this.#onDragMouseEnter);
				drag.removeEventListener('mouseleave', this.#onDragMouseLeave);
			}
			this.drags = null;
		}

		if (this.dots && this.dots.length) {
			for (const dot of this.dots) {
				dot.removeEventListener('mouseenter', this.#onDotMouseEnter);
				dot.removeEventListener('mouseleave', this.#onDotMouseLeave);
			}
		}
	}

	refresh() {
		this.el.container.classList.remove('is-active');
		this.el.container.classList.remove('is-active-dot');
		this.el.container.classList.remove('is-active-drag');
		this.el.container.classList.remove('is-active-drag-no-images');
		this.el.container.classList.remove('is-active-disabled');

		this.targets = document.querySelectorAll('[data-cursor-target]');
		this.drags = document.querySelectorAll('[data-cursor-drag]');
		this.dots = document.querySelectorAll('[data-cursor-dot]');

		if (this.targets.length) {
			for (const target of this.targets) {
				target.addEventListener('mouseenter', this.#onTargetMouseEnter);
				target.addEventListener('mouseleave', this.#onTargetMouseLeave);
			}
		}

		if (this.drags.length) {
			for (const drag of this.drags) {
				drag.addEventListener('mouseenter', this.#onDragMouseEnter);
				drag.addEventListener('mouseleave', this.#onDragMouseLeave);
			}
		}

		if (this.dots.length) {
			for (const dot of this.dots) {
				dot.addEventListener('mouseenter', this.#onDotMouseEnter);
				dot.addEventListener('mouseleave', this.#onDotMouseLeave);
			}
		}
	}

	getTargetBounds(el, offsetY = 0) {
		const { left, top } = getRootOffset(el);
		this.targetX = left + el.clientWidth * 0.5;
		this.targetY = (top - window.scrollY + el.clientHeight * 0.5) + offsetY;
	}

	update() {
		this.targetX = this.mouse.x;
		this.targetY = this.mouse.y;

		if (this.focus) {
			this.getTargetBounds(this.focus);
		}

		if (this.dot) {
			this.getTargetBounds(this.dot, this.dot.clientHeight * 0.6);
		}

		this.scale.value = lerp(this.scale.value, this.scale.target, 0.1);
		this.position.x = lerp(this.position.x, this.targetX, 0.1);
		this.position.y = lerp(this.position.y, this.targetY, 0.1);

		this.el.container.style.transform = `translate3d(${this.position.x}px, ${this.position.y}px, 0) scale3d(${this.scale.value}, ${this.scale.value}, 1)`;
		this.el.container.style.borderWidth = `${2 / this.scale.value}px`;
	}

}
